import type { RepositoryFile } from '@trove/core';
import { fromBase64, toBase64 } from '@trove/crypto';
import { postJson, putJson, api } from './api';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const sha1Hex = async (bytes: Uint8Array): Promise<string> => {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-1', bytes as BufferSource));
  return [...digest].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

const gitObjectId = async (kind: 'blob' | 'tree' | 'commit', body: Uint8Array) => {
  const header = encoder.encode(`${kind} ${body.length}\0`);
  const bytes = new Uint8Array(header.length + body.length);
  bytes.set(header);
  bytes.set(body, header.length);
  return sha1Hex(bytes);
};

export interface OpenCommitPayload {
  tree: string;
  parent: string | null;
  author: string;
  message: string;
  files: Array<{ path: string; blob: string; mimeType: string }>;
  createdAt: string;
}

export async function commitGitSnapshot(input: {
  repositoryId: string;
  files: RepositoryFile[];
  message: string;
  author: string;
  parent?: string | null;
  expectedObjectId?: string | null;
}): Promise<{ commitId: string }> {
  const objects: Array<{ objectId: string; kind: 'blob' | 'tree' | 'commit'; content: string }> =
    [];
  const fileEntries: OpenCommitPayload['files'] = [];

  for (const file of input.files) {
    const content = file.content ?? '';
    const body = encoder.encode(content);
    const objectId = await gitObjectId('blob', body);
    objects.push({ objectId, kind: 'blob', content: toBase64(body) });
    fileEntries.push({
      path: file.path,
      blob: objectId,
      mimeType: file.mimeType || 'text/plain',
    });
  }

  fileEntries.sort((left, right) => left.path.localeCompare(right.path));
  const treeBody = encoder.encode(JSON.stringify({ files: fileEntries }));
  const treeId = await gitObjectId('tree', treeBody);
  objects.push({ objectId: treeId, kind: 'tree', content: toBase64(treeBody) });

  const refs = await api<{ refs: Array<{ name: string; objectId: string }> }>(
    `/v1/git/refs/${input.repositoryId}`,
  );
  const main = refs.refs.find((ref) => ref.name === 'refs/heads/main');
  const parent = input.parent ?? (main && main.objectId !== '0'.repeat(40) ? main.objectId : null);

  const commitPayload: OpenCommitPayload = {
    tree: treeId,
    parent,
    author: input.author,
    message: input.message,
    files: fileEntries,
    createdAt: new Date().toISOString(),
  };
  const commitBody = encoder.encode(JSON.stringify(commitPayload));
  const commitId = await gitObjectId('commit', commitBody);
  objects.push({ objectId: commitId, kind: 'commit', content: toBase64(commitBody) });

  await postJson('/v1/git/objects', {
    repositoryId: input.repositoryId,
    objects,
  });

  await putJson('/v1/git/refs', {
    repositoryId: input.repositoryId,
    name: 'refs/heads/main',
    objectId: commitId,
    expectedObjectId: input.expectedObjectId ?? main?.objectId ?? null,
  });

  return { commitId };
}

export async function loadOpenCommit(
  repositoryId: string,
  commitId: string,
): Promise<{ files: RepositoryFile[]; commit: OpenCommitPayload }> {
  const object = await api<{ objectId: string; kind: string; content: string }>(
    `/v1/git/objects/${repositoryId}/${commitId}`,
  );
  const commit = JSON.parse(decoder.decode(fromBase64(object.content))) as OpenCommitPayload;
  const files: RepositoryFile[] = [];
  for (const entry of commit.files) {
    const blob = await api<{ content: string }>(`/v1/git/objects/${repositoryId}/${entry.blob}`);
    const bytes = fromBase64(blob.content);
    const content = decoder.decode(bytes);
    files.push({
      path: entry.path,
      content,
      mimeType: entry.mimeType,
      size: bytes.byteLength,
      modifiedAt: commit.createdAt,
      contentHash: entry.blob,
    });
  }
  return { files, commit };
}

export async function pullOpenVault(repositoryId: string): Promise<{
  commitId: string | null;
  files: RepositoryFile[];
  commits: OpenCommitPayload[];
}> {
  const refs = await api<{ refs: Array<{ name: string; objectId: string }> }>(
    `/v1/git/refs/${repositoryId}`,
  );
  const main = refs.refs.find((ref) => ref.name === 'refs/heads/main');
  if (!main || main.objectId === '0'.repeat(40)) {
    return { commitId: null, files: [], commits: [] };
  }
  const loaded = await loadOpenCommit(repositoryId, main.objectId);
  const commits: OpenCommitPayload[] = [loaded.commit];
  let parent = loaded.commit.parent;
  while (parent) {
    try {
      const previous = await loadOpenCommit(repositoryId, parent);
      commits.unshift(previous.commit);
      parent = previous.commit.parent;
    } catch {
      break;
    }
  }
  return { commitId: main.objectId, files: loaded.files, commits };
}

export async function searchOpenVault(repositoryId: string, query: string) {
  return postJson<{ matches: Array<{ objectId: string; snippet: string }> }>('/v1/git/search', {
    repositoryId,
    query,
  });
}
